"use client";

import { useRecordIdentityLifecycleSignalOnAction } from "@analytics/lib/use-record-identity-lifecycle-signal-on-action";
import { IDENTITY_LIFECYCLE_SIGNALS } from "@repo/identity";
import { useCallback, useRef } from "react";
import { useDebounceCallback } from "usehooks-ts";
import { MARKETING_EVENTS } from "./marketing-events";
import { useAnalytics } from "./use-analytics";

/** Slider/input churn — settle before PostHog capture (avoids client rate limits). */
const PRICING_ANALYTICS_DEBOUNCE_MS = 400;

export function useMarketingAnalytics() {
	const { trackEvent } = useAnalytics();
	const recordMarketingResonateEngage =
		useRecordIdentityLifecycleSignalOnAction(
			IDENTITY_LIFECYCLE_SIGNALS.marketingResonateEngage,
			"sq:identity:lifecycle:marketing_resonate_engage",
		);

	const trackEventRef = useRef(trackEvent);
	trackEventRef.current = trackEvent;

	const debouncedTrackPricingEvent = useDebounceCallback(
		(event: string, props: Record<string, unknown>) => {
			trackEventRef.current(event, props);
		},
		PRICING_ANALYTICS_DEBOUNCE_MS,
	);

	const landingSectionViewed = useCallback(
		(props: { section_id: string }) => {
			trackEvent(MARKETING_EVENTS.landingSectionViewed, props);
		},
		[trackEvent],
	);

	const landingCtaClicked = useCallback(
		(props: { cta: string; location: string; href?: string }) => {
			trackEvent(MARKETING_EVENTS.landingCtaClicked, props);
		},
		[trackEvent],
	);

	const landingFaqExpanded = useCallback(
		(props: {
			question_index: number;
			question_key: string;
			question_title: string;
		}) => {
			trackEvent(MARKETING_EVENTS.landingFaqExpanded, props);
		},
		[trackEvent],
	);

	const landingSolutionDemoFeatureSelected = useCallback(
		(props: {
			feature_index: number;
			pillar_id: string;
			feature_title: string;
		}) => {
			trackEvent(
				MARKETING_EVENTS.landingSolutionDemoFeatureSelected,
				props,
			);
		},
		[trackEvent],
	);

	const landingSolutionDemoPathsRefreshed = useCallback(
		(props: { feature_index: number; feature_title: string }) => {
			trackEvent(
				MARKETING_EVENTS.landingSolutionDemoPathsRefreshed,
				props,
			);
		},
		[trackEvent],
	);

	const landingSolutionDemoRegenerateClicked = useCallback(
		(props: {
			feature_index: number;
			pillar_id: string;
			feature_title: string;
		}) => {
			trackEvent(
				MARKETING_EVENTS.landingSolutionDemoRegenerateClicked,
				props,
			);
		},
		[trackEvent],
	);

	const landingSolutionDemoRestartClicked = useCallback(() => {
		trackEvent(MARKETING_EVENTS.landingSolutionDemoRestartClicked, {});
	}, [trackEvent]);

	const landingSolutionDemoInteractableOpened = useCallback(
		(props: {
			feature_index: number;
			pillar_id: string;
			feature_title: string;
			topic_index: number;
			topic_key: string;
			topic_title: string;
		}) => {
			trackEvent(
				MARKETING_EVENTS.landingSolutionDemoInteractableOpened,
				props,
			);
		},
		[trackEvent],
	);

	const landingSolutionDemoSessionStarted = useCallback(
		(props: {
			mode: "start" | "resume";
			pillar_id: string;
			feature_index: number;
			feature_title: string;
		}) => {
			trackEvent(
				MARKETING_EVENTS.landingSolutionDemoSessionStarted,
				props,
			);
			recordMarketingResonateEngage();
		},
		[recordMarketingResonateEngage, trackEvent],
	);

	const landingSolutionDemoTopicCompleted = useCallback(
		(props: {
			topic_key: string;
			topic_title: string;
			feature_index: number;
			pillar_id: string;
			feature_title: string;
		}) => {
			trackEvent(
				MARKETING_EVENTS.landingSolutionDemoTopicCompleted,
				props,
			);
		},
		[trackEvent],
	);

	const landingSolutionDemoLevelReached = useCallback(
		(props: { level: number }) => {
			trackEvent(MARKETING_EVENTS.landingSolutionDemoLevelReached, props);
		},
		[trackEvent],
	);

	const landingSolutionDemoSectionCompleted = useCallback(
		(props: {
			pillar_id: string;
			feature_index: number;
			feature_title: string;
		}) => {
			trackEvent(
				MARKETING_EVENTS.landingSolutionDemoSectionCompleted,
				props,
			);
		},
		[trackEvent],
	);

	const landingSolutionDemoAllSectionsCompleted = useCallback(() => {
		trackEvent(
			MARKETING_EVENTS.landingSolutionDemoAllSectionsCompleted,
			{},
		);
	}, [trackEvent]);

	const landingSolutionDemoSoonOverlayViewed = useCallback(() => {
		trackEvent(MARKETING_EVENTS.landingSolutionDemoSoonOverlayViewed, {});
	}, [trackEvent]);

	const landingBackToTopClicked = useCallback(() => {
		trackEvent(MARKETING_EVENTS.landingBackToTopClicked, {});
	}, [trackEvent]);

	const landingColorModeChanged = useCallback(
		(props: { mode: string }) => {
			trackEvent(MARKETING_EVENTS.landingColorModeChanged, props);
		},
		[trackEvent],
	);

	const landingFooterExternalClicked = useCallback(
		(props: { platform: string; href: string }) => {
			trackEvent(MARKETING_EVENTS.landingFooterExternalClicked, props);
		},
		[trackEvent],
	);

	const landingLaunchCountdownAvailabilityClicked = useCallback(
		(props: { href: string }) => {
			trackEvent(
				MARKETING_EVENTS.landingLaunchCountdownAvailabilityClicked,
				props,
			);
		},
		[trackEvent],
	);

	const notFoundViewed = useCallback(
		(props: { surface: string }) => {
			trackEvent(MARKETING_EVENTS.notFoundViewed, props);
		},
		[trackEvent],
	);

	const waitlistSubscribeOutcome = useCallback(
		(props: Record<string, unknown>) => {
			trackEvent(MARKETING_EVENTS.waitlistSubscribeOutcome, props);
		},
		[trackEvent],
	);

	const waitlistVerificationResendOutcome = useCallback(
		(props: Record<string, unknown>) => {
			trackEvent(
				MARKETING_EVENTS.waitlistVerificationResendOutcome,
				props,
			);
		},
		[trackEvent],
	);

	const waitlistVerificationPollResolved = useCallback(
		(props: Record<string, unknown>) => {
			trackEvent(
				MARKETING_EVENTS.waitlistVerificationPollResolved,
				props,
			);
		},
		[trackEvent],
	);

	const waitlistConfirmOutcome = useCallback(
		(props: Record<string, unknown>) => {
			trackEvent(MARKETING_EVENTS.waitlistConfirmOutcome, props);
		},
		[trackEvent],
	);

	const waitlistUnsubscribeOutcome = useCallback(
		(props: Record<string, unknown>) => {
			trackEvent(MARKETING_EVENTS.waitlistUnsubscribeOutcome, props);
		},
		[trackEvent],
	);

	const waitlistResubscribeOutcome = useCallback(
		(props: Record<string, unknown>) => {
			trackEvent(MARKETING_EVENTS.waitlistResubscribeOutcome, props);
		},
		[trackEvent],
	);

	const newsletterSubscribeOutcome = useCallback(
		(props: Record<string, unknown>) => {
			trackEvent(MARKETING_EVENTS.newsletterSubscribeOutcome, props);
		},
		[trackEvent],
	);

	const newsletterVerificationResendOutcome = useCallback(
		(props: Record<string, unknown>) => {
			trackEvent(
				MARKETING_EVENTS.newsletterVerificationResendOutcome,
				props,
			);
		},
		[trackEvent],
	);

	const newsletterVerificationPollResolved = useCallback(
		(props: Record<string, unknown>) => {
			trackEvent(
				MARKETING_EVENTS.newsletterVerificationPollResolved,
				props,
			);
		},
		[trackEvent],
	);

	const newsletterConfirmOutcome = useCallback(
		(props: Record<string, unknown>) => {
			trackEvent(MARKETING_EVENTS.newsletterConfirmOutcome, props);
		},
		[trackEvent],
	);

	const newsletterUnsubscribeOutcome = useCallback(
		(props: Record<string, unknown>) => {
			trackEvent(MARKETING_EVENTS.newsletterUnsubscribeOutcome, props);
		},
		[trackEvent],
	);

	const newsletterResubscribeOutcome = useCallback(
		(props: Record<string, unknown>) => {
			trackEvent(MARKETING_EVENTS.newsletterResubscribeOutcome, props);
		},
		[trackEvent],
	);

	const landingVideoPlayStarted = useCallback(
		(props: { video_kind: string; src: string }) => {
			trackEvent(MARKETING_EVENTS.landingVideoPlayStarted, props);
			recordMarketingResonateEngage();
		},
		[recordMarketingResonateEngage, trackEvent],
	);

	const landingVideoProgress = useCallback(
		(props: {
			milestone: 25 | 50 | 75 | 100;
			video_kind: string;
			src: string;
		}) => {
			trackEvent(MARKETING_EVENTS.landingVideoProgress, props);
		},
		[trackEvent],
	);

	const landingVideoMuted = useCallback(() => {
		trackEvent(MARKETING_EVENTS.landingVideoMuted, {});
	}, [trackEvent]);

	const landingVideoUnmuted = useCallback(() => {
		trackEvent(MARKETING_EVENTS.landingVideoUnmuted, {});
	}, [trackEvent]);

	const landingVideoFullscreenEntered = useCallback(() => {
		trackEvent(MARKETING_EVENTS.landingVideoFullscreenEntered, {});
	}, [trackEvent]);

	const landingVideoFullscreenExited = useCallback(() => {
		trackEvent(MARKETING_EVENTS.landingVideoFullscreenExited, {});
	}, [trackEvent]);

	const landingFeaturesTabSelected = useCallback(
		(props: { tab_index: number; tab_title: string; tab_key: string }) => {
			trackEvent(MARKETING_EVENTS.landingFeaturesTabSelected, props);
		},
		[trackEvent],
	);

	const landingFeaturesLightboxOpened = useCallback(
		(props: {
			slide_index: number;
			slide_count: number;
			tab_index: number;
			tab_title: string;
			tab_key: string;
			screenshot_index: number;
			screenshot_title: string;
		}) => {
			trackEvent(MARKETING_EVENTS.landingFeaturesLightboxOpened, props);
		},
		[trackEvent],
	);

	const landingFeaturesLightboxNavigated = useCallback(
		(props: {
			direction: "next" | "prev";
			slide_index: number;
			slide_count: number;
			tab_index: number;
			tab_title: string;
			tab_key: string;
			screenshot_index: number;
			screenshot_title: string;
		}) => {
			trackEvent(
				MARKETING_EVENTS.landingFeaturesLightboxNavigated,
				props,
			);
		},
		[trackEvent],
	);

	const landingFeaturesScreenshotNavigated = useCallback(
		(props: {
			direction: "next" | "prev" | "breadcrumb";
			tab_index: number;
			tab_title: string;
			tab_key: string;
			screenshot_index: number;
			screenshot_title: string;
			screenshot_count: number;
		}) => {
			trackEvent(
				MARKETING_EVENTS.landingFeaturesScreenshotNavigated,
				props,
			);
		},
		[trackEvent],
	);

	const siteMailtoLinkClicked = useCallback(
		(props: { href: string; page_path: string }) => {
			trackEvent(MARKETING_EVENTS.siteMailtoLinkClicked, props);
		},
		[trackEvent],
	);

	const siteExternalLinkClicked = useCallback(
		(props: { href: string; host: string; page_path: string }) => {
			trackEvent(MARKETING_EVENTS.siteExternalLinkClicked, props);
		},
		[trackEvent],
	);

	const pricingBandChanged = useCallback(
		(props: {
			band_id: string;
			min_seats: number;
			max_seats: number | null;
		}) => {
			trackEvent(MARKETING_EVENTS.pricingBandChanged, props);
		},
		[trackEvent],
	);

	const pricingSeatsChanged = useCallback(
		(props: {
			seat_count: number;
			source: "slider" | "input" | "band";
		}) => {
			if (props.source === "band") {
				trackEvent(MARKETING_EVENTS.pricingSeatsChanged, props);
				return;
			}
			debouncedTrackPricingEvent(
				MARKETING_EVENTS.pricingSeatsChanged,
				props,
			);
		},
		[debouncedTrackPricingEvent, trackEvent],
	);

	const pricingIntervalChanged = useCallback(
		(props: { billing_interval: "WEEK" | "MONTH" | "YEAR" }) => {
			trackEvent(MARKETING_EVENTS.pricingIntervalChanged, props);
		},
		[trackEvent],
	);

	const pricingDisplayModeChanged = useCallback(
		(props: { mode: "per_seat" | "total" }) => {
			trackEvent(MARKETING_EVENTS.pricingDisplayModeChanged, props);
		},
		[trackEvent],
	);

	const pricingSelectionsCleared = useCallback(() => {
		trackEvent(MARKETING_EVENTS.pricingSelectionsCleared, {});
	}, [trackEvent]);

	const pricingPlanSelected = useCallback(
		(props: { plan_sku: string; was_disabled: false }) => {
			trackEvent(MARKETING_EVENTS.pricingPlanSelected, props);
		},
		[trackEvent],
	);

	const pricingContentTabChanged = useCallback(
		(props: { tab: "faqs" | "compare" }) => {
			trackEvent(MARKETING_EVENTS.pricingContentTabChanged, props);
		},
		[trackEvent],
	);

	const pricingFaqExpanded = useCallback(
		(props: { question_index: number; question_title: string }) => {
			trackEvent(MARKETING_EVENTS.pricingFaqExpanded, props);
		},
		[trackEvent],
	);

	const pricingCtaClicked = useCallback(
		(props: {
			location: string;
			cta: string;
			plan_sku: string;
			variant: string;
			slot?: "primary" | "secondary";
			custom?: boolean;
		}) => {
			trackEvent(MARKETING_EVENTS.pricingCtaClicked, props);
		},
		[trackEvent],
	);

	const pricingConsumableChanged = useCallback(
		(props: {
			dimension: "credits" | "media_writes" | "storage";
			value: number;
			source: "slider" | "input" | "stepper";
		}) => {
			debouncedTrackPricingEvent(
				MARKETING_EVENTS.pricingConsumableChanged,
				props,
			);
		},
		[debouncedTrackPricingEvent],
	);

	const pricingFreeGated = useCallback(
		(props: {
			credits: number;
			media_writes: number;
			storage_gb: number;
		}) => {
			debouncedTrackPricingEvent(
				MARKETING_EVENTS.pricingFreeGated,
				props,
			);
		},
		[debouncedTrackPricingEvent],
	);

	const pricingQuotePreviewShown = useCallback(
		(props: {
			plan_sku: string;
			has_boosters: boolean;
			custom: boolean;
		}) => {
			debouncedTrackPricingEvent(
				MARKETING_EVENTS.pricingQuotePreviewShown,
				props,
			);
		},
		[debouncedTrackPricingEvent],
	);

	const pricingCheckoutStarted = useCallback(
		(props: {
			plan_sku: string;
			seat_count: number;
			has_boosters: boolean;
			quote_id: string;
		}) => {
			trackEvent(MARKETING_EVENTS.pricingCheckoutStarted, props);
		},
		[trackEvent],
	);

	const pricingCheckoutRejected = useCallback(
		(props: { plan_sku: string; reason: string }) => {
			trackEvent(MARKETING_EVENTS.pricingCheckoutRejected, props);
		},
		[trackEvent],
	);

	const checkoutGateOpened = useCallback(
		(props: {
			plan_sku: string;
			change_execution: string;
			quote_id?: string;
		}) => {
			trackEvent(MARKETING_EVENTS.checkoutGateOpened, props);
		},
		[trackEvent],
	);

	const checkoutConfirmStarted = useCallback(
		(props: {
			plan_sku: string;
			change_execution: string;
			path: "elements" | "deferred" | "immediate_zero";
		}) => {
			trackEvent(MARKETING_EVENTS.checkoutConfirmStarted, props);
		},
		[trackEvent],
	);

	const checkoutConfirmSucceeded = useCallback(
		(props: {
			plan_sku: string;
			change_execution: string;
			path: "elements" | "deferred" | "immediate_zero";
		}) => {
			trackEvent(MARKETING_EVENTS.checkoutConfirmSucceeded, props);
		},
		[trackEvent],
	);

	const checkoutConfirmFailed = useCallback(
		(props: {
			plan_sku: string;
			change_execution: string;
			path: "elements" | "deferred" | "immediate_zero";
			error_code?: string | null;
		}) => {
			trackEvent(MARKETING_EVENTS.checkoutConfirmFailed, props);
		},
		[trackEvent],
	);

	const checkoutSuccessPageViewed = useCallback(
		(props: { kind: string; organization_slug: string }) => {
			trackEvent(MARKETING_EVENTS.checkoutSuccessPageViewed, props);
		},
		[trackEvent],
	);

	const checkoutProvisioningReadyConfirmed = useCallback(
		(props: { kind: string; organization_slug: string }) => {
			trackEvent(
				MARKETING_EVENTS.checkoutProvisioningReadyConfirmed,
				props,
			);
		},
		[trackEvent],
	);

	const checkoutCanceledConfirmed = useCallback(
		(props: {
			organization_slug: string;
			session_status: string;
			payment_status: string;
			quote_id?: string;
		}) => {
			trackEvent(MARKETING_EVENTS.checkoutCanceledConfirmed, props);
		},
		[trackEvent],
	);

	const billingCancelAtPeriodEndConfirmed = useCallback(
		(props: { organization_id: string; plan_sku_key: string }) => {
			trackEvent(
				MARKETING_EVENTS.billingCancelAtPeriodEndConfirmed,
				props,
			);
		},
		[trackEvent],
	);

	const billingUncancelAtPeriodEndConfirmed = useCallback(
		(props: { organization_id: string; plan_sku_key: string }) => {
			trackEvent(
				MARKETING_EVENTS.billingUncancelAtPeriodEndConfirmed,
				props,
			);
		},
		[trackEvent],
	);

	const billingDeferredChangeCanceledConfirmed = useCallback(
		(props: { organization_id: string; schedule_id: string }) => {
			trackEvent(
				MARKETING_EVENTS.billingDeferredChangeCanceledConfirmed,
				props,
			);
		},
		[trackEvent],
	);

	const billingCollectionPreferenceChangedConfirmed = useCallback(
		(props: {
			organization_id: string;
			preference: "invoice" | "automatic";
		}) => {
			trackEvent(
				MARKETING_EVENTS.billingCollectionPreferenceChangedConfirmed,
				props,
			);
		},
		[trackEvent],
	);

	return {
		landingSectionViewed,
		landingCtaClicked,
		landingFaqExpanded,
		landingSolutionDemoFeatureSelected,
		landingSolutionDemoPathsRefreshed,
		landingSolutionDemoRegenerateClicked,
		landingSolutionDemoRestartClicked,
		landingSolutionDemoInteractableOpened,
		landingSolutionDemoSessionStarted,
		landingSolutionDemoTopicCompleted,
		landingSolutionDemoLevelReached,
		landingSolutionDemoSectionCompleted,
		landingSolutionDemoAllSectionsCompleted,
		landingSolutionDemoSoonOverlayViewed,
		landingBackToTopClicked,
		landingColorModeChanged,
		landingFooterExternalClicked,
		landingLaunchCountdownAvailabilityClicked,
		landingVideoPlayStarted,
		landingVideoProgress,
		landingVideoMuted,
		landingVideoUnmuted,
		landingVideoFullscreenEntered,
		landingVideoFullscreenExited,
		landingFeaturesTabSelected,
		landingFeaturesLightboxOpened,
		landingFeaturesLightboxNavigated,
		landingFeaturesScreenshotNavigated,
		siteMailtoLinkClicked,
		siteExternalLinkClicked,
		notFoundViewed,
		waitlistSubscribeOutcome,
		waitlistVerificationResendOutcome,
		waitlistVerificationPollResolved,
		waitlistConfirmOutcome,
		waitlistUnsubscribeOutcome,
		waitlistResubscribeOutcome,
		newsletterSubscribeOutcome,
		newsletterVerificationResendOutcome,
		newsletterVerificationPollResolved,
		newsletterConfirmOutcome,
		newsletterUnsubscribeOutcome,
		newsletterResubscribeOutcome,
		pricingBandChanged,
		pricingSeatsChanged,
		pricingIntervalChanged,
		pricingDisplayModeChanged,
		pricingSelectionsCleared,
		pricingPlanSelected,
		pricingContentTabChanged,
		pricingFaqExpanded,
		pricingCtaClicked,
		pricingConsumableChanged,
		pricingFreeGated,
		pricingQuotePreviewShown,
		pricingCheckoutStarted,
		pricingCheckoutRejected,
		checkoutGateOpened,
		checkoutConfirmStarted,
		checkoutConfirmSucceeded,
		checkoutConfirmFailed,
		checkoutSuccessPageViewed,
		checkoutProvisioningReadyConfirmed,
		checkoutCanceledConfirmed,
		billingCancelAtPeriodEndConfirmed,
		billingUncancelAtPeriodEndConfirmed,
		billingDeferredChangeCanceledConfirmed,
		billingCollectionPreferenceChangedConfirmed,
	};
}
